"use client";

import { useRef, useState } from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { IMG_BASE } from "@/lib/api";
import { useSiteContext } from "@/components/providers/SiteContextProvider";
import { getTranslations } from "@/lib/i18n";

// Maps the site locale to the BCP-47 tag used by toLocaleString for the
// "last updated" date so the month name matches the page language.
const DATE_LOCALE = {
  nl: "nl-NL",
  pl: "pl-PL",
  de: "de-DE",
  fr: "fr-FR",
  en: "en-GB",
};

/**
 * Calendar images carousel for voucher detail page.
 */
/**
 * Renders the "last updated" date text after hydration to avoid
 * server/client mismatch from toLocaleString() timezone differences.
 */
function LastUpdatedText({ lastUpdated, locale, t }) {
  const [formattedDate] = useState(() =>
    lastUpdated
      ? new Date(lastUpdated).toLocaleString(DATE_LOCALE[locale] || "nl-NL", {
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
      : "",
  );

  return (
    <p>
      {t(
        "voucher.calendar.disclaimer",
        "Bij een 0 in de kalender kan de voucher ingewisseld worden zonder bij te betalen. Op sommige dagen of bij specifiek door u gekozen locaties, upgrades, extra's en/of een ander reisgezelschap kan een bijbetaling voor het hele reisgezelschap gelden. Deze totale bijbetaling moet bij het inwisselen van de voucher bijbetaald worden. Bovenstaande beschikbaarheid en prijzen zijn voor het laatst bijgewerkt op",
      )}{" "}
      {formattedDate && <span suppressHydrationWarning>{formattedDate}</span>}
    </p>
  );
}

/**
 * 2026-04-30 (Dennis 15:26 #team — "2 keer staat de afbeelding op de
 * voucher"): the standalone campaign-image block in
 * `app/(marketing)/vouchers/[id]/page.js` (line ~617) ALREADY renders
 * `campaignImageUrl` independently of this calendar carousel. Rendering
 * it here too caused the campaign image to appear twice on every
 * voucher detail page that had a campaign image set (verified on
 * /vouchers/phantasialand-special-... — 2× same `<img alt="Campagne
 * afbeelding">` in the rendered HTML). The duplicate is dropped here;
 * the carousel now only renders the calendar images. The page-level
 * standalone block remains the single source for campaign-image
 * rendering.
 */
export default function CalendarCarousel({ calendarImages = [], lastUpdated }) {
  const scrollRef = useRef(null);
  const { locale } = useSiteContext();
  const { t } = getTranslations(locale);

  if (!calendarImages.length) return null;

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const scrollAmount = scrollRef.current.offsetWidth * 0.7;
    scrollRef.current.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  return (
    <section className="space-y-4">
      {/* Campaign image is intentionally NOT rendered here — see component
          docblock; the standalone block in vouchers/[id]/page.js owns it. */}

      {/* Calendar images */}
      {calendarImages.length > 0 && (
        <div className="border border-gray-200 rounded-lg p-4 sm:p-6 bg-[#f4f8fc]">
          <div className="flex items-center gap-2 mb-4 text-black">
            <Calendar className="w-5 h-5" />
            <h2 className="text-lg sm:text-[1.7rem] font-semibold">
              {t("voucher.calendar.heading", "Reiskalender & beschikbaarheid")}
            </h2>
          </div>

          <div className="relative">
            <button
              onClick={() => scroll("left")}
              className="absolute left-1 top-1/2 -translate-y-1/2 z-10 bg-white text-black w-8 h-8 rounded-full shadow flex items-center justify-center hover:bg-gray-50 transition-colors"
              aria-label={t("voucher.calendar.prev", "Vorige")}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => scroll("right")}
              className="absolute right-1 top-1/2 -translate-y-1/2 z-10 bg-white text-black w-8 h-8 rounded-full shadow flex items-center justify-center hover:bg-gray-50 transition-colors"
              aria-label={t("voucher.calendar.next", "Volgende")}
            >
              <ChevronRight className="w-4 h-4" />
            </button>

            <div
              ref={scrollRef}
              className="flex gap-3 overflow-x-auto scroll-smooth px-8 pb-2"
              // snap-by-panel: same voucher-calendar layout invariant as the
              // live AvailabilityCalendar (this is the static-image variant
              // of the "Reiskalender & beschikbaarheid" section).
              // scrollPaddingLeft mirrors the px-8 (2rem) container padding.
              style={{ scrollbarWidth: "none", msOverflowStyle: "none", scrollSnapType: "x mandatory", scrollPaddingLeft: "2rem" }}
            >
              {calendarImages.map((img, i) => {
                // Kartik #107 (2026-04-28): fe#441 unwraps calendar
                // images to plain strings before passing them in
                // (`.map(img => typeof img === "string" ? img : img?.url || img)`).
                // The old code only knew the `{url}` object shape, so on
                // strings `img.url` was undefined and we built
                // `${IMG_BASE}/` (empty path) — every cell rendered
                // as a blank grey banner on every voucher without
                // travel_start/end_date (Harry Potter, etc.). Accept
                // both shapes here so the component is resilient to
                // either caller.
                const rawUrl =
                  typeof img === "string" ? img : (img?.url ?? "");
                if (!rawUrl) return null;
                const src = rawUrl.startsWith("http")
                  ? rawUrl
                  : `${IMG_BASE}/${rawUrl.replace(/^\/+/, "")}`;
                return (
                  <div
                    key={i}
                    className="flex-shrink-0 w-[260px] sm:w-[300px] rounded-lg overflow-hidden shadow-sm"
                    style={{ scrollSnapAlign: "start" }}
                  >
                    <Image
                      src={src}
                      alt={t("voucher.calendar.image_alt", "Kalender {n}", { n: i + 1 })}
                      width={300}
                      height={200}
                      className="w-full h-auto object-cover"
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="text-[1rem] text-gray-600 leading-relaxed mt-5">
            {lastUpdated ? (
              <LastUpdatedText lastUpdated={lastUpdated} locale={locale} t={t} />
            ) : (
              <p>{t("voucher.calendar.no_update", "Geen update beschikbaar.")}</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
